import TOSModal from '@components/alert/TOSModal.component';
import { FullPageLoader } from '@components/loaders/FullPageLoader.component';
import { useAuth, useAuthActions, useAuthUserOrNull } from '@frontegg/react';
import { AuthState } from '@frontegg/redux-store';
import ConfigService from '@services/config/config.service';
import FeatureFlagService from '@services/featureFlags/featureFlag.service';
import * as LDClient from 'launchdarkly-js-client-sdk';
import React, { createContext, useState, PropsWithChildren, useEffect, useMemo, useCallback } from 'react';
import { redirect, useNavigate, useSearchParams } from 'react-router-dom';

const PATHS_WITH_NO_CONFIG = [
    "/signup",
    "/welcome",
    "/create-account",
    "/unable-to-connect",
    "/tenant-not-found",
  ];

export interface AuthServiceState {
    plbAuthState: PlbAuthState | null;
    isAuthenticated: boolean;
    authStateUpdatedAt: number;
}

export interface PlbAuthState {
    sub: string;
    name: string;
    email: string;
    tenant_id: string;
    roles: Array<string>
    tenant_name: string;
    access_token: string;
    profile_img_url?: string;
    tenants?: Array<{
      tenantId: string;
      name: string;
    }>;
    // Optional field for custom claims
    customClaims?: Record<string, any>;
}

export default class AuthService {
    private static instance: AuthService;

    plbAuthState: PlbAuthState | null = null;
    authState: AuthState | null;
    authStateUpdatedAt: number;
    isAuthenticated: boolean;
    authUrl: string;
    tenantName: string | null;
    feClientId: string | undefined;
    feAppId: string | undefined;
    ldClient: LDClient.LDClient | null;
    ldClientId: string | undefined;
    subscribers: Array<Function>;

    constructor() {
        // @ts-ignore
        this.authState = {
            isLoading: true,
            isAuthenticated: false,
        };
        this.isAuthenticated = false;
        this.authStateUpdatedAt = new Date().getTime();
        this.tenantName = null;
        this.ldClient = null;

        this.authUrl = '';
        this.feClientId = undefined;
        this.feAppId = undefined;

        this.subscribers = [];
        const host = window.location.host;
        if (host.startsWith('local.')) {
            this.authUrl = "https://auth-dev.whetstonedata.com";
            this.feClientId = '692d0655-fa36-4d1a-8dde-8d45c64bf1ef';
            this.feAppId = '72fa4f93-2543-4600-ba03-7de81decd30a';
            this.ldClientId = '67d055939e55d809955930fb';
        } else if (host == "snowflake.pliable.co") {
            this.authUrl = `https://auth.app.pliable.co`;
            this.feClientId = 'b1c62231-3a21-48ed-857e-cde8ce0c2303';
            this.feAppId = '75c936eb-5473-4aae-a3cf-0f3edefc55e8';
        } else if (host == "dev-beta.pliable.co") {
            this.authUrl = `https://auth.${window.location.host}`
            this.feClientId = '512d51ea-262c-496b-8aeb-7644d1dc12dd';
            this.feAppId = '07a2c076-3948-4aa0-ae6f-851bfec6ddff';
            this.ldClientId = '67db334b41f39109971063f9';
        } else if (host.startsWith('app.')) {
            this.authUrl = "https://auth.app.pliable.co";
            this.feClientId = 'b1c62231-3a21-48ed-857e-cde8ce0c2303';
            this.ldClientId = '67d055939e55d809955930fc';
            this.feAppId = 'da24bc6c-0351-4e5f-b262-91d924f6de71';
        } else {
            this.authUrl = `https://auth.${window.location.host}`;
        }

        // Initialize LaunchDarkly client with a default context
        const defaultContext: LDClient.LDContext = {
            kind: 'user',
            key: 'anonymous',
            name: 'Anonymous',
            email: 'anonymous@example.com',
            custom: {
                tenant: 'Tenant'
            }
        };
        if (this.ldClientId) {
            this.ldClient = LDClient.initialize(this.ldClientId, defaultContext);
        } else {
            console.error('LaunchDarkly client ID is not defined');
        }
    }


    public setAuthState(plbAuthState: PlbAuthState) {
        this.plbAuthState = plbAuthState;
        this.tenantName = plbAuthState.tenant_name;
        
        this.updateAnalytics(plbAuthState);
        this.updateThena(plbAuthState);
        this.updateLaunchDarkly(plbAuthState);
        this.isAuthenticated = true;
        this._notifySubscribers();
    }

    getState() : AuthServiceState {
        return {
            plbAuthState: this.plbAuthState,
            isAuthenticated: this.isAuthenticated,
            authStateUpdatedAt: this.authStateUpdatedAt,
        }
    }

    public createPlbAuthStateFromFrontEgg(frontEggAuthState: any): PlbAuthState {
        // Extract the tenants array from FrontEgg's auth state
        const tenants = frontEggAuthState?.tenantsState?.tenants?.map((t: any) => ({
          tenantId: t.tenantId,
          name: t.name
        })) || [];
    
        // Find the current tenant name
        const currentTenant = tenants.find((t: any) => t.tenantId === frontEggAuthState?.user?.tenantId);
        
        return {
          sub: frontEggAuthState?.user?.id || '',
          name: frontEggAuthState?.user?.name || '',
          email: frontEggAuthState?.user?.email || '',
          profile_img_url: frontEggAuthState?.user?.profilePictureUrl || '',
          tenant_id: frontEggAuthState?.user?.tenantId || '',
          roles: frontEggAuthState?.user?.roles || [],
          tenant_name: currentTenant?.name || 'Tenant',
          access_token: frontEggAuthState?.user?.accessToken,
          tenants,
          customClaims: frontEggAuthState?.user?.customClaims || {}
        };
    }

    private updateAnalytics(authState: PlbAuthState) {
        try {
          // Log rocket needs an identify call on each page load or the session shows up anonymous
          window.analytics?.identify(authState.sub, {
            name: authState.name,
            email: authState.email,
          });
        } catch (ex) {
          console.log(ex);
          //pass
        }
      }
    
      private updateThena(authState: PlbAuthState) {
        try {
          (window as any).thena?.setEmail({
            email: authState.email,
            hashedEmail: authState.customClaims?.thena_hmac,
          });
        } catch (ex) {
          console.log(ex);
        }
      }
    
      private updateLaunchDarkly(authState: PlbAuthState) {
        // Update LaunchDarkly context with authenticated user information
        const context: any = {
          kind: 'user',
          key: authState.sub,
          name: authState.name,
          email: authState.email,
          custom: {
            tenant: authState.tenant_name
          }
        };
    
        this.ldClient?.identify(context)
          .then(() => {
            FeatureFlagService.getInstance().initialize(this);
          })
          .catch((error: any) => {
            console.error('Error updating LaunchDarkly context:', error);
          });
      }
    
      // Getter methods
    public getAuthState(): PlbAuthState | null {
        return this.plbAuthState;
    }

    getAccessToken() {
        return this.plbAuthState?.access_token;
    }

    getTenantName() {
        return this.tenantName;
    }

    getTenantId() {
        return this.plbAuthState?.tenant_id;
    }

    hasRole(role: string) {
        if (!this.authState?.isAuthenticated) {
            return false;
        }

        return this.authState?.user?.roles.some(r => r.key === role)
    }

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }

        return AuthService.instance;
    }

    async getUserTenants() {
        const response = await fetch(this.authUrl + '/identity/resources/users/v2/me/tenants', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.getAccessToken(),
            },
            mode: 'cors',
            credentials: 'include',
        });
        return await response.json();
    }

    subscribe(callback: Function) {
        this.subscribers.push(callback);

        // Notify the new one about the current state
        callback(this.getState());
        return () => {
          this.subscribers = this.subscribers.filter(cb => cb !== callback);
        };
      }
    
      // Call this whenever state changes
      _notifySubscribers() {
        const state = this.getState();
        this.subscribers.forEach(callback => callback(state));
      }
}

export const useAuthState = () => {
    return AuthService.getInstance().plbAuthState;
}

export const useFullAuthState = () => {
    const service = AuthService.getInstance();
    return {
        authState: service.plbAuthState,
        isAuthenticated: service.isAuthenticated,
        authStateUpdatedAt: service.authStateUpdatedAt,
    }
}

const AuthContext = createContext<null|AuthServiceState>(null);


const ONBOARDING_PATH_PREFIXES = ['/onboarding', '/connect']

export const AuthProvider = (props: PropsWithChildren<{}>) => {
    const fronteggUser = useAuthUserOrNull();
    const fronteggAuthState = useAuth();
    const [loadingConfig, setLoadingConfig] = useState(true);
    const { switchTenant } = useAuthActions();
    const [searchParams, setSearchParams] = useSearchParams();
    const [authState, setAuthState] = useState(AuthService.getInstance().getState());

    const isReady = useMemo(() => {
        console.log('Is loading', fronteggAuthState.isLoading, 'loadingConfig', loadingConfig);
        return !fronteggAuthState.isLoading && !loadingConfig;
    }, [fronteggAuthState.isLoading, loadingConfig])

    const navigate = useNavigate();

    const redirectToOnboarding = useCallback(() => {
        const returnPath = localStorage.getItem('returnPath');
        console.log('Redirecting to onboarding. Return path is:', returnPath);
        if (returnPath) {
            // We're redirecting so remove it from local storage so they're not caught in a loop
            localStorage.removeItem('returnPath');
        }
        if (returnPath && (window.location.pathname + window.location.search) != returnPath) {
            navigate(returnPath);
        } else if (!returnPath && !ONBOARDING_PATH_PREFIXES.some(prefix => window.location.pathname.startsWith(prefix))) {
            navigate('/onboarding');
        }
    }, [navigate]);

    const handleAuthentication = useCallback(() => {
        const authService = AuthService.getInstance();
        
        // Check for our own token first
        const _sessionState = sessionStorage.getItem('plb_auth_state');
        let plbAuthState: PlbAuthState | null = null;
        
        if (_sessionState) {
            // Create auth state from our custom token
            plbAuthState = JSON.parse(_sessionState);
        } else if (fronteggUser && fronteggAuthState) {
            // Create auth state from FrontEgg
            plbAuthState = authService.createPlbAuthStateFromFrontEgg(fronteggAuthState);
        }
        
        // If we couldn't establish auth state, redirect to login
        if (!plbAuthState) {
            navigate('/login');
            return;
        }
        
        // Set our auth state
        authService.setAuthState(plbAuthState);
        
        // Skip config loading for certain paths
        if (PATHS_WITH_NO_CONFIG.includes(window.location.pathname)) {
            setLoadingConfig(false);
            return;
        }
        
        // Handle tenant switching if needed
        const inbound_tenant_id = searchParams.get('inbound_tenant_id');
        if (!!inbound_tenant_id && plbAuthState.tenant_id != inbound_tenant_id) {
            switchTenant({ tenantId: inbound_tenant_id });
            navigate(window.location.pathname);
            return;
        }
        
        loadConfig();
    }, [fronteggUser, fronteggAuthState, navigate, searchParams, switchTenant]);

    const loadConfig = useCallback(async () => {
        setLoadingConfig(true);
        const service = ConfigService.getInstance();

        // These need to happen in series because loadConfig will lazy-load the tenant registration which affects how we load
        // everything from Mongo.
        await service.loadConfig();
        await service.loadUserConfig();
        setLoadingConfig(false);

        // Here is where we handle redirects to the onboarding flow
        const config = ConfigService.getInstance().config;
        if (!config) {
            console.error('No config after loading');
            return;
        }
        if (config.is_disabled) {
            navigate('/account-disabled');
            return;

        }

        if (!config.database || !config.infra.is_ready) {
            redirectToOnboarding();
            return;
        }
        
    }, [redirectToOnboarding]);

    useEffect(() => {
        const unsubscribe = AuthService.getInstance().subscribe((state: AuthServiceState) => {
            //console.log('Subscription got new state', state);
            setAuthState(state);
        });

        handleAuthentication();
        return () => unsubscribe(); 
    }, []);

    return <AuthContext.Provider value={authState}>
        {isReady && fronteggAuthState.isAuthenticated && <TOSModal/>}
        {!isReady && <FullPageLoader/>}
        {isReady && props.children}
    </AuthContext.Provider>
}
